import { AnchorTitle } from "#components/ui/anchor-title"
import { Callout } from "#components/ui/callout"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "#components/ui/card"
import { Separator } from "#components/ui/separator"
import { Body, H3 } from "#components/ui/typography"
import { useI18n } from "#hooks/use-i18n"
import { Calendar, Filter, RefreshCw, Sparkles } from "lucide-react"

export function DashboardHelp() {
  const { t } = useI18n()

  return (
    <div className="space-y-6">
      <div>
        <H3 className="mb-2">{t("pages.help.dashboard.overview.title")}</H3>
        <Body className="text-muted-foreground">
          {t("pages.help.dashboard.overview.description")}
        </Body>
      </div>

      <Card id="dashboard-header-actions">
        <CardHeader>
          <CardTitle>
            <AnchorTitle id="dashboard-header-actions">
              {t("pages.help.dashboard.headerActions.title")}
            </AnchorTitle>
          </CardTitle>
          <CardDescription>{t("pages.help.dashboard.headerActions.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
            <div className="flex-1">
              <Body className="font-semibold mb-1">
                {t("pages.help.dashboard.headerActions.dateRange.title")}
              </Body>
              <Body className="text-muted-foreground text-sm">
                {t("pages.help.dashboard.headerActions.dateRange.description")}
              </Body>
            </div>
          </div>

          <Separator />

          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
            <div className="flex-1">
              <Body className="font-semibold mb-1">
                {t("pages.help.dashboard.headerActions.virtualExpenses.title")}
              </Body>
              <Body className="text-muted-foreground text-sm">
                {t("pages.help.dashboard.headerActions.virtualExpenses.description")}
              </Body>
            </div>
          </div>

          <Separator />

          <div className="flex items-start gap-3">
            <RefreshCw className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
            <div className="flex-1">
              <Body className="font-semibold mb-1">
                {t("pages.help.dashboard.headerActions.refresh.title")}
              </Body>
              <Body className="text-muted-foreground text-sm">
                {t("pages.help.dashboard.headerActions.refresh.description")}
              </Body>
            </div>
          </div>

          <Separator />

          <div className="flex items-start gap-3">
            <Filter className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
            <div className="flex-1">
              <Body className="font-semibold mb-1">
                {t("pages.help.dashboard.headerActions.filters.title")}
              </Body>
              <Body className="text-muted-foreground text-sm">
                {t("pages.help.dashboard.headerActions.filters.description")}
              </Body>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card id="dashboard-global-summary">
        <CardHeader>
          <CardTitle>
            <AnchorTitle id="dashboard-global-summary">
              {t("pages.help.dashboard.globalSummary.title")}
            </AnchorTitle>
          </CardTitle>
          <CardDescription>{t("pages.help.dashboard.globalSummary.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.dashboard.globalSummary.expenseTrend.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.dashboard.globalSummary.expenseTrend.description")}
            </Body>
          </div>

          <Separator />

          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.dashboard.globalSummary.partnerContributions.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.dashboard.globalSummary.partnerContributions.description")}
            </Body>
          </div>

          <Separator />

          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.dashboard.globalSummary.remainingBalance.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.dashboard.globalSummary.remainingBalance.description")}
            </Body>
          </div>
        </CardContent>
      </Card>

      <Card id="dashboard-sankey">
        <CardHeader>
          <CardTitle>
            <AnchorTitle id="dashboard-sankey">
              {t("pages.help.dashboard.sankey.title")}
            </AnchorTitle>
          </CardTitle>
          <CardDescription>{t("pages.help.dashboard.sankey.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Callout variant="tip">
            <p>
              <strong>Tip:</strong> Hover over nodes and links to see detailed amounts and
              percentages.
            </p>
          </Callout>

          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.dashboard.sankey.leftSide.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.dashboard.sankey.leftSide.description")}
            </Body>
          </div>

          <Separator />

          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.dashboard.sankey.rightSide.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.dashboard.sankey.rightSide.description")}
            </Body>
          </div>

          <Separator />

          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.dashboard.sankey.flowLines.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.dashboard.sankey.flowLines.description")}
            </Body>
          </div>
        </CardContent>
      </Card>

      <Card id="dashboard-monthly-expenses">
        <CardHeader>
          <CardTitle>
            <AnchorTitle id="dashboard-monthly-expenses">
              {t("pages.help.dashboard.monthlyExpenses.title")}
            </AnchorTitle>
          </CardTitle>
          <CardDescription>{t("pages.help.dashboard.monthlyExpenses.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.dashboard.monthlyExpenses.cumulativeChart.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.dashboard.monthlyExpenses.cumulativeChart.description")}
            </Body>
          </div>

          <Separator />

          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.dashboard.monthlyExpenses.dailyChart.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.dashboard.monthlyExpenses.dailyChart.description")}
            </Body>
          </div>
        </CardContent>
      </Card>

      <Card id="dashboard-expense-sources">
        <CardHeader>
          <CardTitle>
            <AnchorTitle id="dashboard-expense-sources">
              {t("pages.help.dashboard.expenseSources.title")}
            </AnchorTitle>
          </CardTitle>
          <CardDescription>{t("pages.help.dashboard.expenseSources.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.dashboard.expenseSources.pieChart.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.dashboard.expenseSources.pieChart.description")}
            </Body>
          </div>

          <Separator />

          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.dashboard.expenseSources.listView.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.dashboard.expenseSources.listView.description")}
            </Body>
          </div>
        </CardContent>
      </Card>

      <Card id="dashboard-monthly-balance">
        <CardHeader>
          <CardTitle>
            <AnchorTitle id="dashboard-monthly-balance">
              {t("pages.help.dashboard.monthlyBalance.title")}
            </AnchorTitle>
          </CardTitle>
          <CardDescription>{t("pages.help.dashboard.monthlyBalance.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.dashboard.monthlyBalance.areaChart.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.dashboard.monthlyBalance.areaChart.description")}
            </Body>
          </div>

          <Separator />

          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.dashboard.monthlyBalance.monthlyList.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.dashboard.monthlyBalance.monthlyList.description")}
            </Body>
          </div>
        </CardContent>
      </Card>

      <Card id="dashboard-partner-contribution">
        <CardHeader>
          <CardTitle>
            <AnchorTitle id="dashboard-partner-contribution">
              {t("pages.help.dashboard.partnerContribution.title")}
            </AnchorTitle>
          </CardTitle>
          <CardDescription>
            {t("pages.help.dashboard.partnerContribution.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.dashboard.partnerContribution.barChart.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.dashboard.partnerContribution.barChart.description")}
            </Body>
          </div>

          <Separator />

          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.dashboard.partnerContribution.ratioAnalysis.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.dashboard.partnerContribution.ratioAnalysis.description")}
            </Body>
          </div>
        </CardContent>
      </Card>

      <Card id="dashboard-top-expenses">
        <CardHeader>
          <CardTitle>
            <AnchorTitle id="dashboard-top-expenses">
              {t("pages.help.dashboard.topExpenses.title")}
            </AnchorTitle>
          </CardTitle>
          <CardDescription>{t("pages.help.dashboard.topExpenses.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Body className="text-muted-foreground text-sm">
            {t("pages.help.dashboard.topExpenses.content")}
          </Body>
        </CardContent>
      </Card>

      <Card id="dashboard-category-comparison">
        <CardHeader>
          <CardTitle>
            <AnchorTitle id="dashboard-category-comparison">
              {t("pages.help.dashboard.categoryComparison.title")}
            </AnchorTitle>
          </CardTitle>
          <CardDescription>
            {t("pages.help.dashboard.categoryComparison.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Body className="text-muted-foreground text-sm">
            {t("pages.help.dashboard.categoryComparison.content")}
          </Body>
        </CardContent>
      </Card>
    </div>
  )
}
